import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { getSolidStatusBgColor } from '@odo/shared-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSearchScope } from '../context/CommandPaletteContext';
import { useResourcePageTitle } from '../hooks/useResourcePageTitle';
import { useResourcesLibrary } from '../providers/resourcesLibraryProvider';
import { CreateResourceButton, EditResourceButton } from '../authoring/AuthoringButtons';
import InternalBadge from '../components/InternalBadge';
import PinButton from '../components/PinButton';
import RailListLayout from '../components/RailListLayout';
import RichTextBody from '../components/RichTextBody';
import ResourcesPageHeader from '../components/ResourcesPageHeader';
import { ResourceEmpty, ResourceError, ResourceLoading } from '../components/ResourceStates';
import type { FaqItemData } from '../providers/faqProvider';

/** Questions with no category still need a heading to sit under. */
const UNCATEGORISED = 'General';

interface AnswerGroup {
    category: string;
    /** Anchor target for deep-linked questions. */
    slug: string;
    items: FaqItemData[];
}

function categorySlug(category: string): string {
    return `answers-${category
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')}`;
}

const FaqQuestionCard: React.FC<{
    item: FaqItemData;
    anchorId: string;
    isOpen: boolean;
    onToggle: () => void;
}> = ({ item, anchorId, isOpen, onToggle }) => (
    <article
        id={anchorId}
        className="bg-white border border-gray-200 rounded-xl shadow-sm scroll-mt-6 hover:border-gray-300 hover:shadow transition-all"
    >
        <div className="flex items-center gap-2 px-6">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isOpen}
                className="flex-1 min-w-0 flex items-center gap-3 py-5 text-left group"
            >
                <span className="flex-1 min-w-0 text-[15px] font-bold text-gray-800 group-hover:text-gray-900">
                    {item.question}
                </span>
                <InternalBadge audience={item.audience} />
                <ChevronDownIcon
                    className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                />
            </button>
            <EditResourceButton
                kind="faq"
                item={item}
                name={item.question}
                className="flex-shrink-0"
            />
            <PinButton kind="faq" id={item.id} title={item.question} className="flex-shrink-0" />
        </div>

        {isOpen && (
            <div className="mx-6 mb-5 rounded-lg border border-gray-100 bg-gray-50">
                <RichTextBody
                    value={item.answer}
                    fallback="markdown"
                    className="prose prose-sm prose-slate max-w-none px-5 py-4 text-gray-600 leading-relaxed"
                />
            </div>
        )}
    </article>
);

/** FAQ: one card per question, collapsed by default so a long answer never buries the rest. */
export const FaqPage: React.FC = () => {
    const { faqItems, isLoading, error, refresh } = useResourcesLibrary();
    const { hash } = useLocation();
    const [category, setCategory] = useState<string | null>(null);
    const [openIds, setOpenIds] = useState<ReadonlySet<number>>(new Set());

    const toggle = (id: number) =>
        setOpenIds((current) => {
            const next = new Set(current);
            if (!next.delete(id)) next.add(id);
            return next;
        });

    useResourcePageTitle('FAQ');
    useSearchScope('faq');

    const groups = useMemo<AnswerGroup[]>(() => {
        const byCategory = new Map<string, FaqItemData[]>();
        for (const item of faqItems) {
            // A question in two categories appears under both — the rail is a
            // browse aid, not an exclusive taxonomy.
            const categories = item.category.length > 0 ? item.category : [UNCATEGORISED];
            for (const category of categories) {
                const bucket = byCategory.get(category);
                if (bucket) bucket.push(item);
                else byCategory.set(category, [item]);
            }
        }
        return Array.from(byCategory.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([category, items]) => ({
                category,
                slug: categorySlug(category),
                items: [...items].sort((a, b) => a.question.localeCompare(b.question))
            }));
    }, [faqItems]);

    const visibleGroups = useMemo(
        () => (category ? groups.filter((group) => group.category === category) : groups),
        [groups, category]
    );

    // A ⌘K result for an answer lands here with `#faq-<id>`: open that row, then
    // scroll to it. Without the open step the anchor lands on a collapsed row.
    useEffect(() => {
        if (!hash || faqItems.length === 0) return;
        const id = Number(hash.replace('#faq-', ''));
        if (Number.isFinite(id)) setOpenIds((current) => new Set(current).add(id));
        const target = document.getElementById(hash.slice(1));
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [hash, faqItems.length]);

    const body = () => {
        if (isLoading && faqItems.length === 0) return <ResourceLoading label="Loading FAQs…" />;
        if (error && faqItems.length === 0) {
            return <ResourceError message={error} onRetry={() => refresh(true)} />;
        }
        if (groups.length === 0) {
            return (
                <ResourceEmpty
                    title="No FAQs yet"
                    description="Check back later, or ask the clinical team directly in Conversations."
                />
            );
        }

        return (
            <RailListLayout
                showFrom="lg"
                innerClassName="flex flex-col gap-6"
                railItems={[
                    {
                        key: 'all',
                        label: 'All',
                        count: faqItems.length,
                        active: category === null,
                        onSelect: () => setCategory(null)
                    },
                    ...groups.map((group) => ({
                        key: group.slug,
                        label: group.category,
                        count: group.items.length,
                        active: category === group.category,
                        onSelect: () => setCategory(group.category),
                        dotClassName: getSolidStatusBgColor(group.category)
                    }))
                ]}
            >
                {visibleGroups.map((group) => (
                    <section
                        key={group.slug}
                        id={group.slug}
                        className="flex flex-col gap-3 scroll-mt-6"
                    >
                        <div className="flex items-baseline gap-2.5">
                            <h2 className="text-base font-bold text-gray-800">{group.category}</h2>
                            <span className="text-sm text-gray-500">
                                {group.items.length}{' '}
                                {group.items.length === 1 ? 'question' : 'questions'}
                            </span>
                        </div>

                        <div className="flex flex-col gap-2">
                            {group.items.map((item) => (
                                <FaqQuestionCard
                                    key={`${group.slug}-${item.id}`}
                                    item={item}
                                    anchorId={`faq-${item.id}`}
                                    isOpen={openIds.has(item.id)}
                                    onToggle={() => toggle(item.id)}
                                />
                            ))}
                        </div>
                    </section>
                ))}
            </RailListLayout>
        );
    };

    return (
        <div className="flex flex-col min-h-0 flex-1">
            <ResourcesPageHeader
                title="Frequently Asked Questions"
                searchPlaceholder="Search the FAQ…"
                actions={<CreateResourceButton kind="faq" />}
            />
            <div className="flex-1 min-h-0 px-8 pt-6 pb-8">{body()}</div>
        </div>
    );
};

export default FaqPage;
